import React from 'react'
import {useIntl} from 'react-intl'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import clsx from 'clsx'
import {KTIcon} from '../../../../helpers'
import {downloadSenderDocument} from '../../../../../app/pages/senderApproval/_requests'

dayjs.extend(localizedFormat)

type Props = {
  senderRecord: any
  onApprove: () => void
  onReject: () => void
}

const DrawerSenderDetails: React.FC<Props> = ({senderRecord, onApprove, onReject}) => {
  const intl = useIntl()

  const handleDownload = async (doc: {name: string; url: string}) => {
    try {
      const blob = await downloadSenderDocument(senderRecord.uuid)
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = doc.name
      a.click()
      URL.revokeObjectURL(objUrl)
    } catch (err) {
      console.error('Download failed', err)
    }
  }

  return (
    <div
      id='sender-details'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='sender-details'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'300px', 'lg': '900px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#sender-details-toggle'
      data-kt-drawer-close='#sender-details-close'
    >
      <div className='card shadow-none rounded-0 w-100 h-100 d-flex flex-column'>
        <div className='card-header' id='sender-details-header'>
          <h3 className='card-title fw-bolder text-dark d-flex align-items-center'>
            {intl.formatMessage({id: 'SENDER.DETAILS_TITLE', defaultMessage: 'Sender Details'})}
            {senderRecord && (
              <span
                className={clsx('badge ms-3 fs-7', {
                  'badge-light-success': senderRecord.status === 'approved',
                  'badge-light-danger': senderRecord.status === 'rejected',
                  'badge-light-warning': senderRecord.status === 'pending',
                })}
              >
                {senderRecord.status === 'approved'
                  ? intl.formatMessage({id: 'COMMON.APPROVED', defaultMessage: 'Approved'})
                  : senderRecord.status === 'rejected'
                  ? intl.formatMessage({id: 'COMMON.REJECTED', defaultMessage: 'Rejected'})
                  : intl.formatMessage({id: 'COMMON.PENDING', defaultMessage: 'Pending'})}
              </span>
            )}
          </h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-light-primary me-n5'
              id='sender-details-close'
            >
              <KTIcon iconName='cross' className='fs-1' />
            </button>
          </div>
        </div>

        <div className='card-body position-relative' id='sender-details-body'>
          <div
            id='sender-details-scroll'
            className='position-relative scroll-y me-n5 pe-5'
            data-kt-scroll='true'
            data-kt-scroll-height='auto'
            data-kt-scroll-wrappers='#sender-details-body'
            data-kt-scroll-dependencies='#sender-details-header, #sender-details-footer'
            data-kt-scroll-offset='5px'
          >
            {senderRecord ? (
              <>
                {/* Sender Information */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'SENDER.INFO_SECTION', defaultMessage: 'Sender Information'})}
                  </h5>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'TABLE.SENDER_NAME', defaultMessage: 'Sender Name'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{senderRecord.name}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'SENDER.SENDER_TYPE', defaultMessage: 'Sender Type'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{senderRecord.senderType || '—'}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.CATEGORY', defaultMessage: 'Category'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{senderRecord.category || '—'}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'TABLE.OWNER_ID', defaultMessage: 'Organisation ID'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{senderRecord.ownerId ?? '—'}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'TABLE.REGISTERED_ON', defaultMessage: 'Registered On'})}
                      </div>
                      <div className='fw-bold text-gray-800'>
                        {dayjs(senderRecord.created_at).format('DD/MM/YYYY')}
                        <span className='d-block text-muted fw-semibold fs-7'>
                          {dayjs(senderRecord.created_at).format('LT')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Supporting Document */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'SENDER.DOCUMENTS', defaultMessage: 'Supporting Document'})}
                  </h5>
                  {senderRecord.documents?.length > 0 ? (
                    senderRecord.documents.map((doc: any, index: number) => (
                      <div
                        key={index}
                        className='d-flex align-items-center justify-content-between border border-dashed border-gray-300 rounded px-6 py-4 mb-3'
                      >
                        <div className='d-flex align-items-center'>
                          <KTIcon iconName='file-down' className='fs-2 text-primary me-3' />
                          <span className='fw-bold text-gray-700'>{doc.name}</span>
                        </div>
                        {doc.url && (
                          <div className='d-flex gap-2'>
                            <a
                              href={doc.url}
                              target='_blank'
                              rel='noreferrer'
                              className='btn btn-sm btn-icon btn-light btn-active-light-primary'
                              title={intl.formatMessage({id: 'KYC.VIEW_DOCUMENT', defaultMessage: 'View Document'})}
                            >
                              <KTIcon iconName='eye' className='fs-3' />
                            </a>
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-light btn-active-light-success'
                              title='Download'
                              onClick={() => handleDownload(doc)}
                            >
                              <KTIcon iconName='file-down' className='fs-3' />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className='text-muted fs-7 py-2'>
                      {intl.formatMessage({id: 'SENDER.NO_DOCUMENTS', defaultMessage: 'No documents uploaded'})}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className='text-gray-500 text-center py-10'>
                {intl.formatMessage({id: 'SENDER.NO_RECORD', defaultMessage: 'No record selected'})}
              </div>
            )}
          </div>
        </div>

        <div className='card-footer py-5 border-top-0' id='sender-details-footer'>
          <div className='d-flex justify-content-end gap-3'>
            <button
              type='button'
              disabled={!senderRecord || senderRecord.status === 'approved'}
              className='btn btn-success'
              onClick={onApprove}
            >
              <KTIcon iconName='check' className='fs-3 me-1' />
              {intl.formatMessage({id: 'SENDER.APPROVE', defaultMessage: 'Approve Sender'})}
            </button>
            <button
              type='button'
              disabled={!senderRecord || senderRecord.status === 'rejected'}
              className='btn btn-danger'
              onClick={onReject}
            >
              <KTIcon iconName='cross' className='fs-3 me-1' />
              {intl.formatMessage({id: 'SENDER.REJECT', defaultMessage: 'Reject Sender'})}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export {DrawerSenderDetails}
